// src/shared/telemetry/SignalStatsTable.tsx
// ────────────────────────────────────────────────────────────────────────────
// Unified signal statistics table — supports BOTH modes:
//   • Flat mode:       Single-source metric rows (DaemonOps per-magnitude)
//   • Grouped mode:    Collapsible group-header + per-device sub-rows (IoT fleet)
//
// PAC Glass design system — dark glass, monospace numerics.
// ────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react'
import type { MetricGroup } from './types'
import { fmtStat } from './telemetryUtils'
import type { TimeSeriesPoint } from '@/shared/components/plots/TelemetryTimeSeriesPlot'
import { computeSignalStat, type SignalStat } from './telemetryUtils'

/* ── Props ──────────────────────────────────────────────────────────── */

interface FlatConfig {
  key: string
  title: string
  unit: string
  color: string
}

export interface SignalStatsTableProps {
  /** Grouped mode — collapsible per-source breakdown (IoT fleet). */
  groups?: MetricGroup[]
  /** Flat mode — simple stats rows from raw series (DaemonOps single-source). */
  flatConfigs?: FlatConfig[]
  flatSeries?: Record<string, TimeSeriesPoint[]>
  /** Optional title for the table header. */
  title?: string
}

/* ── Columns ────────────────────────────────────────────────────────── */

const STAT_COLS = ['', 'MIN', 'MAX', 'Δ', 'AVG', 'PTS'] as const

/* ── Component ──────────────────────────────────────────────────────── */

export default function SignalStatsTable({ groups, flatConfigs, flatSeries, title }: SignalStatsTableProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  const toggle = (metric: string) =>
    setCollapsed(prev => ({ ...prev, [metric]: !prev[metric] }))

  // Flat mode: build simple row data from series
  const flatRows = useMemo<SignalStat[]>(() => {
    if (!flatConfigs || !flatSeries) return []
    return flatConfigs
      .map(cfg => computeSignalStat(cfg.title, cfg.color, flatSeries[cfg.key] ?? []))
      .filter(Boolean) as SignalStat[]
  }, [flatConfigs, flatSeries])

  const isGrouped = !!groups && groups.length > 0
  const isFlat = flatRows.length > 0

  if (!isGrouped && !isFlat) return null

  // Decide which columns to show — Pts only for flat mode
  const cols = isGrouped
    ? (['', 'MIN', 'MAX', 'Δ', 'AVG'] as const)
    : STAT_COLS

  return (
    <div className="shrink-0 overflow-x-auto pac-glass rounded-lg">
      {title && (
        <div className="px-3 py-2 border-b border-white/5">
          <span className="text-[10px] uppercase tracking-wider text-neutral-500">{title}</span>
        </div>
      )}
      <table className="w-full text-xs font-mono border-collapse">
        <thead>
          <tr>
            {cols.map(c => (
              <th
                key={c || '_signal'}
                className={`py-1.5 px-3 text-[10px] uppercase tracking-wider text-neutral-500 font-medium
                  ${c === '' ? 'text-left' : 'text-right'}`}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* ── Grouped mode (IoT: collapsible per-device) ─────────── */}
          {isGrouped && groups!.map(g => {
            const isCol = collapsed[g.metric] ?? false
            return (
              <React.Fragment key={g.metric}>
                <tr
                  className="border-t border-white/[0.08] cursor-pointer hover:bg-white/[0.03] transition-colors"
                  onClick={() => toggle(g.metric)}
                >
                  <td className="py-2 px-3 text-left">
                    <span className="flex items-center gap-2">
                      <span className="text-neutral-500 text-[10px] w-3 select-none">
                        {isCol ? '▸' : '▾'}
                      </span>
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: g.color }}
                      />
                      <span className="text-neutral-200 font-semibold">{g.metric}</span>
                      <span className="text-neutral-500 text-[10px]">[{g.unit}]</span>
                      <span className="text-neutral-600 text-[10px] ml-1">
                        {g.sources.length} source{g.sources.length > 1 ? 's' : ''}
                      </span>
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right text-neutral-200 font-semibold">{fmtStat(g.min)}</td>
                  <td className="py-2 px-3 text-right text-neutral-200 font-semibold">{fmtStat(g.max)}</td>
                  <td className="py-2 px-3 text-right text-neutral-200 font-semibold">{fmtStat(g.delta)}</td>
                  <td className="py-2 px-3 text-right text-neutral-200 font-semibold">{fmtStat(g.avg)}</td>
                </tr>
                {!isCol && g.sources.map(d => (
                  <tr key={d.label} className="border-t border-white/[0.03]">
                    <td className="py-1 px-3 pl-10 text-left">
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block w-1.5 h-1.5 rounded-full opacity-70"
                          style={{ backgroundColor: d.color }}
                        />
                        <span className="text-neutral-400">{d.label}</span>
                      </span>
                    </td>
                    <td className="py-1 px-3 text-right text-neutral-400">{fmtStat(d.min)}</td>
                    <td className="py-1 px-3 text-right text-neutral-400">{fmtStat(d.max)}</td>
                    <td className="py-1 px-3 text-right text-neutral-400">{fmtStat(d.delta)}</td>
                    <td className="py-1 px-3 text-right text-neutral-400">{fmtStat(d.avg)}</td>
                  </tr>
                ))}
              </React.Fragment>
            )
          })}

          {/* ── Flat mode (DaemonOps: simple rows) ─────────────────── */}
          {isFlat && flatRows.map(r => (
            <tr key={r.label} className="border-t border-white/[0.03]">
              <td className="py-1.5 px-3 text-left text-neutral-300">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: r.color }} />
                  {r.label}
                </span>
              </td>
              <td className="py-1.5 px-3 text-right text-neutral-400">{fmtStat(r.min)}</td>
              <td className="py-1.5 px-3 text-right text-neutral-400">{fmtStat(r.max)}</td>
              <td className="py-1.5 px-3 text-right text-neutral-400">{fmtStat(r.delta)}</td>
              <td className="py-1.5 px-3 text-right text-neutral-400">{fmtStat(r.avg)}</td>
              <td className="py-1.5 px-3 text-right text-neutral-500">{r.pts.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
